import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../constants/constants';
import { TaskSections } from '../constants/tasks';
import { getItemField } from '../constants/utilityFunctions';
import { useNavigationStack } from '../context/NavigationStackContext';
import { useTask } from '../context/TaskContext';
import { useTransaction } from '../context/TransactionContext';
import { useEmailCategoryTagger } from './useEmailCategoryTagger';
import { IMatter } from '../interface/matter';
import { ITask } from '../interface/tasks';
import { ITransaction } from '../interface/transaction';
import { ItemDetails, MailMetadata } from '../interface/mailMetadata';
import { showToast } from '../components/utils/toast';

interface UseTransactionMailActionsParams {
  selectedMatter: IMatter | undefined;
  matterDetails: IMatter | undefined;
  mailSubject: string;
  sentDate: string;
  itemDetails: ItemDetails | null;
  mailMetadata: MailMetadata | null;
  fetchMetaData: () => void;
  setExpanded: (value: boolean) => void;
  onLinkSuccess?: () => void;
  onValidateRestrictions?: (transaction: ITransaction) => Promise<unknown>;
}

export const useTransactionMailActions = ({
  selectedMatter,
  matterDetails,
  mailSubject,
  sentDate,
  itemDetails,
  fetchMetaData,
  setExpanded,
  onLinkSuccess,
  onValidateRestrictions,
}: UseTransactionMailActionsParams) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const { updateTransaction } = useTransaction();
  const { updateTask, clearInitialTask } = useTask();
  const { tagEmailWithCategory, untagEmailCategory, getItemId } = useEmailCategoryTagger();
  const [linkToMatterLoading, setLinkToMatterLoading] = useState(false);

  const hasSelectedMatter = (matter: IMatter | undefined): matter is IMatter => {
    if (!matter) {
      showToast('info', t('matter:selectMatterToContinue'));
      return false;
    }
    return true;
  };

  const passesMatterRestrictions = async () => {
    if (!onValidateRestrictions) return true;
    const result = await onValidateRestrictions({
      matter: selectedMatter,
      matterId: selectedMatter?.id,
    } as ITransaction);
    return Boolean(result);
  };

  const handleAddTransaction = async () => {
    if (!hasSelectedMatter(selectedMatter)) return;
    if (!matterDetails) return;
    if (!(await passesMatterRestrictions())) return;

    const item = Office.context.mailbox.item;
    const isAppointment = item?.itemType === Office.MailboxEnums.ItemType.Appointment;
    let startTime: string | undefined =
      sentDate && dayjs(sentDate).isValid() ? sentDate : dayjs().toISOString();
    const dateTimeSent = (item as unknown as Record<string, unknown>)?.dateTimeSent as
      Date | undefined;
    const isDraft = !isAppointment && (!dateTimeSent || !dayjs(dateTimeSent).isValid());

    if (isAppointment) {
      startTime = await getItemField(item as unknown as Record<string, unknown>, 'start');
    }

    updateTransaction({
      matterId: selectedMatter.id,
      matter: matterDetails,
      subject: mailSubject,
      currentSubject: mailSubject,
      transactionDate: dayjs(startTime).format('YYYY-MM-DD'),
      isManualUnitPrice: false,
      shouldLoadServerPrice: true,
      principalCompany: matterDetails?.principalCompany,
      principalPrivatePerson: matterDetails?.principalPrivatePerson,
      overrideWarnings: true,
      isFromMailOrAppointment: true,
      disableMatterChange: true,
      isDraft,
    } as ITransaction);

    push(ROUTES.ADD_EDIT_TRANSACTION);
  };

  const formatTaskTime = (
    isAppointment: boolean,
    type: string,
    time: string | undefined,
    date: string,
  ) => {
    if (isAppointment) {
      return type === TaskSections.generalTasks
        ? dayjs(time).format('YYYY-MM-DD')
        : dayjs(time).format();
    }
    return type === TaskSections.generalTasks
      ? dayjs(date).format('YYYY-MM-DD')
      : dayjs(date).format();
  };

  const handleAddTask = async (type: string) => {
    if (!hasSelectedMatter(selectedMatter)) return;
    if (!matterDetails) return;
    if (!(await passesMatterRestrictions())) return;

    const item = Office.context.mailbox.item;
    const isAppointment = item?.itemType === Office.MailboxEnums.ItemType.Appointment;
    let startTime: string | undefined;
    let endTime: string | undefined;
    let alertTime: unknown;

    if (isAppointment) {
      startTime = await getItemField(item as unknown as Record<string, unknown>, 'start');
      endTime = await getItemField(item as unknown as Record<string, unknown>, 'end');
      const reminderMinutes = itemDetails?.reminderMinutesBeforeStart;
      if (startTime && typeof reminderMinutes === 'number' && itemDetails?.isReminderOn) {
        alertTime = dayjs(startTime).subtract(reminderMinutes, 'minute').toISOString();
      }
    }

    const formattedStartTime = formatTaskTime(isAppointment, type, startTime, sentDate);
    const formattedDueTime = isAppointment
      ? formatTaskTime(isAppointment, type, endTime, sentDate)
      : undefined;

    updateTask(null);
    clearInitialTask();
    updateTask({
      matterId: selectedMatter.id,
      matter: matterDetails,
      subject: mailSubject,
      currentSubject: mailSubject,
      startTime: formattedStartTime,
      isFromMailOrAppointment: true,
      disableMatterChange: true,
      alertTime: alertTime as string,
      ...(formattedDueTime && { dueTime: formattedDueTime }),
    } as ITask);

    push(type === TaskSections.generalTasks ? ROUTES.ADD_EDIT_TASK : ROUTES.ADD_EDIT_CRITICAL_TASK);
  };

  const handleLinkToMatter = async () => {
    if (!hasSelectedMatter(selectedMatter)) return;
    if (!(await passesMatterRestrictions())) return;

    const matterNumber = `${selectedMatter.matterNumber}`;
    setLinkToMatterLoading(true);
    tagEmailWithCategory(`[CSI - ${t('matter:matter')} - ${matterNumber}]`, {
      matterId: selectedMatter.id,
    })
      .then(() => {
        setExpanded(false);
        showToast('success', t('matter:matterLinkedSuccessfully'));
        onLinkSuccess?.();
        fetchMetaData();
      })
      .finally(() => {
        setLinkToMatterLoading(false);
      });
  };

  const handleUnlinkToMatter = async () => {
    const item = Office.context.mailbox.item;
    const itemId = item?.itemId ?? (await getItemId(item));
    if (itemId) {
      setLinkToMatterLoading(true);
      await untagEmailCategory(new RegExp(String.raw`\[csi - ${t('matter:matter')}`, 'i'), {
        matterId: '',
      })
        .then(() => {
          showToast('success', t('matter:matterUnlinkedSuccessfully'));
        })
        .finally(() => {
          setLinkToMatterLoading(false);
        });
      fetchMetaData();
    }
  };

  return {
    linkToMatterLoading,
    handleAddTransaction,
    handleAddTask,
    handleLinkToMatter,
    handleUnlinkToMatter,
  };
};
